import debounce from "lodash.debounce";
import { apiFetch } from "../../core/utils/api";

const isPlainObject = (value) =>
  value != null && typeof value === "object" && !Array.isArray(value);

// Deep merge for plain objects (arrays are replaced).
// This allows multiple schedule() calls within the debounce window to accumulate fields,
// preventing "last payload wins" from dropping earlier field changes.
const deepMerge = (target, source) => {
  if (!isPlainObject(target)) return isPlainObject(source) ? { ...source } : source;
  if (!isPlainObject(source)) return { ...target };

  const result = { ...target };
  for (const [key, sv] of Object.entries(source)) {
    const tv = target[key];
    if (isPlainObject(tv) && isPlainObject(sv)) {
      result[key] = deepMerge(tv, sv);
    } else {
      result[key] = sv;
    }
  }
  return result;
};

function makeRequest(endpoint, payload, method = "PATCH") {
  if (process.env.NODE_ENV === "development" && endpoint.includes("/api/items/")) {
    try {
      console.debug('[HERO_PATTERN_DEBUG] autosave request', { endpoint, method, payload });
    } catch (err) {
      console.debug('[HERO_PATTERN_DEBUG] autosave logging failed', err);
    }
  }
  return apiFetch(endpoint, {
    method: method,
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
    credentials: "include",
  });
}

export function createAutosave(delay = 700) {
  // Per-autosave-instance state (kept scoped to avoid cross-instance leakage).
  // Each endpoint-method pair has:
  // - a debounced "ready to send" trigger
  // - a single in-flight request (serialized)
  // - a pending payload accumulator (deep-merged)
  // - the latest known server version (used to prevent 412 conflicts)
  const entries = new Map();

  const ensureEntry = (cacheKey) => {
    if (entries.has(cacheKey)) return entries.get(cacheKey);

    const entry = {
      endpoint: null,
      method: "PATCH",
      pendingPayload: null,
      readyToSend: false,
      inFlight: false,
      lastKnownVersion: null,
      callbacks: { onSuccess: null, onError: null },
      debouncedMarkReady: null,
    };

    entry.debouncedMarkReady = debounce(() => {
      entry.readyToSend = true;
      kick(cacheKey);
    }, delay);

    entries.set(cacheKey, entry);
    return entry;
  };

  const kick = async (cacheKey) => {
    const entry = entries.get(cacheKey);
    if (!entry) return;
    if (entry.inFlight) return;
    if (!entry.readyToSend) return;
    if (!entry.pendingPayload || typeof entry.pendingPayload !== "object") return;

    entry.inFlight = true;
    entry.readyToSend = false;

    const snapshot = entry.pendingPayload;
    entry.pendingPayload = null;

    const payloadToSend = { ...snapshot };
    if (payloadToSend.version != null && entry.lastKnownVersion != null) {
      payloadToSend.version = entry.lastKnownVersion;
    }

    let requestSucceeded = false;
    try {
      const res = await makeRequest(entry.endpoint, payloadToSend, entry.method);

      if (!res.ok) {
        const errorText = await res.text();
        const errorCb = entry.callbacks?.onError;

        try {
          const errorJson = JSON.parse(errorText);
          if (errorCb) errorCb({ status: res.status, response: errorJson, message: errorText });
        } catch (_) {
          if (errorCb) errorCb({ status: res.status, response: errorText, message: errorText });
        }

        // Restore the snapshot into pending so changes are not dropped.
        // Newer pending changes (if any) should win on key collisions.
        entry.pendingPayload = deepMerge(snapshot, entry.pendingPayload || {});
        return;
      }

      let json = {};
      try {
        json = await res.json();
      } catch (_) {
        // No JSON body (e.g., 204 or empty 200) – proceed with empty object
      }

      const serverVersion = Number(json?.version);
      if (Number.isFinite(serverVersion)) {
        entry.lastKnownVersion = serverVersion;
      }

      const successCb = entry.callbacks?.onSuccess;
      if (successCb) successCb(json || {});
      requestSucceeded = true;
    } catch (err) {
      const errorCb = entry.callbacks?.onError;
      if (errorCb) errorCb(err);

      // Restore the snapshot into pending so changes are not dropped.
      entry.pendingPayload = deepMerge(snapshot, entry.pendingPayload || {});
    } finally {
      entry.inFlight = false;
      // Only auto-drain queued saves after a successful request.
      // On errors we avoid immediate retry loops; the next schedule() (or debounce fire)
      // will trigger another attempt.
      if (requestSucceeded && entry.readyToSend && entry.pendingPayload) {
        kick(cacheKey);
      }
    }
  };

  const schedule = (endpoint, payload, onSuccess, onError, method = "PATCH") => {
    const cacheKey = `${endpoint}-${method}`;
    const entry = ensureEntry(cacheKey);

    entry.endpoint = endpoint;
    entry.method = method;
    entry.callbacks = { onSuccess, onError };

    // Track the highest known version seen (either from the payload or later from responses).
    const v = Number(payload?.version);
    if (Number.isFinite(v)) {
      entry.lastKnownVersion =
        entry.lastKnownVersion == null ? v : Math.max(entry.lastKnownVersion, v);
    }

    entry.pendingPayload = deepMerge(entry.pendingPayload || {}, payload || {});
    entry.debouncedMarkReady();
  };

  const hasPendingPayload = (endpoint, method = "PATCH") => {
    const cacheKey = `${endpoint}-${method}`;
    const entry = entries.get(cacheKey);
    if (!entry) return false;
    return !!(entry.pendingPayload && Object.keys(entry.pendingPayload).length > 0);
  };

  const getPendingFieldNames = (endpoint, method = "PATCH") => {
    const cacheKey = `${endpoint}-${method}`;
    const entry = entries.get(cacheKey);
    if (!entry || !entry.pendingPayload || typeof entry.pendingPayload !== "object") return [];
    return Object.keys(entry.pendingPayload);
  };

  return { schedule, hasPendingPayload, getPendingFieldNames };
} 